import { DestroyRef, Injectable, ViewContainerRef, effect, inject, signal, untracked } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { BrnDialogService } from '@spartan-ng/brain/dialog';
import type { LightboxItem } from '@lite-workspace/shared/photo-lightbox';
import { BalancePopupsLauncherService } from '../../account/ui/balance-popups-launcher.service';
import { DESIGNS_DATA } from '../domain/infrastructure/designs.data';
import { type MyTemplateRow, toMyTemplate } from '../domain/infrastructure/partner-shop.resource';
import type { MyTemplate } from '../domain/entities/design.models';
import { PartnerPackPreviewDialogComponent, type PartnerPackPreviewContext } from './partner-pack-preview-dialog.component';
import { templateLightboxItems } from './template-lightbox-items';
import {
  TemplateGrantedDialogComponent,
  type TemplateGrantedDialogContext,
  type TemplateGrantedResult,
} from './template-granted-dialog.component';

/**
 * The studio confirms a payment while nobody is looking, so the news waits for the next visit and the
 * cabinet shell — the one surface a photographer always lands on — is what triggers it. One licence per
 * visit: whatever else was confirmed keeps its turn for the visit after.
 *
 * It asks the wire itself rather than through `PartnerShopResource`: that resource opens the templates
 * screen's reads on injection, and this launcher lives in EVERY visit, on every page.
 */
@Injectable({ providedIn: 'root' })
export class TemplateGrantedLauncherService {
  private readonly http = inject(HttpClient);
  private readonly dialog = inject(BrnDialogService);
  private readonly data = inject(DESIGNS_DATA);
  private readonly balancePopups = inject(BalancePopupsLauncherService);

  private readonly cabinet = signal<ViewContainerRef | null>(null);
  private asked = false;

  constructor() {
    effect(() => {
      const vcr = this.cabinet();
      // Оба автоокна кабинета живут на ОДНОМ ViewContainerRef: пока баланс держит свой alertdialog,
      // окно выдачи ждёт — иначе оно встаёт поверх окна, которое нельзя закрыть.
      if (!vcr || this.asked || this.balancePopups.popupOpen()) return;
      this.asked = true;
      untracked(() => void this.announce(vcr));
    });
  }

  /** Called by the cabinet shell — its view container hosts the window and dies with the cabinet. */
  registerCabinet(vcr: ViewContainerRef, destroyRef: DestroyRef): void {
    this.cabinet.set(vcr);
    destroyRef.onDestroy(() => this.cabinet.set(null));
  }

  /** A failed read is silent by design: nothing to show, and no error the photographer did not ask for.
   *  Но флаг «уже спрашивали» снимается на каждом отказе: иначе одна сетевая осечка съедала бы новость
   *  о выданной лицензии на всю сессию. */
  private async announce(vcr: ViewContainerRef): Promise<void> {
    let granted: MyTemplate[];
    try {
      const rows = await firstValueFrom(this.http.get<MyTemplateRow[]>('/api/template-licenses/unacknowledged'));
      granted = rows.map(toMyTemplate);
    } catch {
      this.asked = false;
      return;
    }

    // Показывать нечего — ответ получен, второй раз за визит не спрашиваем.
    const template = granted[0];
    if (!template) return;

    // Окно открывается СОБРАННЫМ: развороты читаются до `open`, иначе оно дорастает миниатюрами и
    // уводит кнопки из-под курсора. Отказ чтения — просто окно без полосы миниатюр.
    let items: readonly LightboxItem[];
    try {
      items = templateLightboxItems(await this.data.getTemplates(template.id));
    } catch {
      items = [];
    }

    // The cabinet may have died while the read was on the wire — its view container is gone with it.
    if (this.cabinet() !== vcr) {
      this.asked = false;
      return;
    }

    // Барьер стоит ЗДЕСЬ, а не только в эффекте. Между проверкой в эффекте и этой строкой лежат два
    // сетевых ожидания, а окно пополнения открывается своей независимой цепочкой: успей оно внутри этого
    // окна — окно выдачи встало бы поверх `disableClose`-alertdialog, ровно того, что обещано исключить.
    // Флаг снимается: новость не потеряна, следующий монтаж кабинета переспросит.
    if (this.balancePopups.popupOpen()) {
      this.asked = false;
      return;
    }

    this.open(vcr, template, items);
  }

  private open(vcr: ViewContainerRef, template: MyTemplate, items: readonly LightboxItem[]): void {
    const ref = this.dialog.open<TemplateGrantedDialogContext, TemplateGrantedResult>(
      TemplateGrantedDialogComponent,
      vcr,
      { template, items },
      { hasBackdrop: true },
    );

    ref.closed$.subscribe(result => {
      // Closing IS the receipt. A failed confirmation is left alone: the licence stays unacknowledged
      // and the announcement simply comes back on the next visit.
      void firstValueFrom(this.http.post(`/api/template-licenses/${template.licenseId}/acknowledge`, {})).catch(() => undefined);
      if (result === 'template') this.openPreview(vcr, template);
    });
  }

  /** «Перейти к шаблону» — no navigation anywhere: which project the pack belongs in is not this
   *  window's question. It hands over to the very viewer a card in «Мои шаблоны» opens, on the page
   *  the photographer is already standing on. */
  private openPreview(vcr: ViewContainerRef, template: MyTemplate): void {
    this.dialog.open<PartnerPackPreviewContext>(PartnerPackPreviewDialogComponent, vcr, {
      pack: template,
      loadSpreads: () => this.data.getTemplates(template.id),
      // Лицензия уже выдана и именно этой новостью открыто окно — меняться ей внутри просмотра нечем.
      licence: signal(template).asReadonly(),
    });
  }
}
